import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { FaChevronDown, FaChevronUp, FaComment } from 'react-icons/fa';
import { boardService } from '../services/boardService';
import { boardReplyCount, stripHtmlToText } from '../board/boardDisplayUtils';
import { ClubBoardThreadHeader } from './ClubBoardThreadHeader';
import { ClubBoardReplyList } from './ClubBoardReplyList';
import { ClubBoardReplyComposer } from './ClubBoardReplyComposer';

export function ClubBoardPostCard({
  thread,
  clubId,
  currentUser,
  expanded = false,
  onToggleExpand,
  onReplyPosted,
}) {
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replySaving, setReplySaving] = useState(false);

  const listReplyCount = boardReplyCount(thread);
  const loadedPostCount = detail?.postCount ?? 0;
  const loadedReplyCount = Math.max(0, loadedPostCount - 1);
  const replyCount = expanded && detail ? loadedReplyCount : listReplyCount;

  const loadDetail = useCallback(async () => {
    if (!clubId || !thread?.id) return;
    setLoadingDetail(true);
    setDetailError('');
    try {
      const res = await boardService.getThread(clubId, thread.id, { limit: 50, offset: 0 });
      const posts = res.posts || [];
      setDetail({
        posts,
        postCount: typeof res.count === 'number' ? res.count : posts.length,
      });
    } catch {
      setDetailError('Failed to load replies');
    } finally {
      setLoadingDetail(false);
    }
  }, [clubId, thread.id]);

  useEffect(() => {
    if (!expanded) {
      setDetail(null);
      setDetailError('');
      setReplyOpen(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoadingDetail(true);
      setDetailError('');
      try {
        const res = await boardService.getThread(clubId, thread.id, { limit: 50, offset: 0 });
        if (!cancelled) {
          const posts = res.posts || [];
          setDetail({
            posts,
            postCount: typeof res.count === 'number' ? res.count : posts.length,
          });
        }
      } catch {
        if (!cancelled) setDetailError('Failed to load replies');
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [expanded, clubId, thread.id]);

  const firstPost = detail?.posts?.[0];
  const replies = detail?.posts?.slice(1) || [];
  const bodyText = firstPost
    ? (firstPost.body_text || stripHtmlToText(firstPost.body_html))
    : '';

  const handleReply = async () => {
    const trimmed = replyText.trim();
    if (!trimmed) return;
    setReplySaving(true);
    try {
      const escaped = trimmed
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
      await boardService.createPost(clubId, thread.id, {
        body_html: `<p>${escaped}</p>`,
      });
      setReplyText('');
      setReplyOpen(false);
      await loadDetail();
      onReplyPosted?.(thread.id);
    } catch {
      setDetailError('Failed to post reply');
    } finally {
      setReplySaving(false);
    }
  };

  const handleReplyKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleReply();
    }
  };

  const openReply = () => {
    setReplyOpen(true);
    if (!expanded) onToggleExpand?.();
  };

  return (
    <article
      className={[
        'club-board-post',
        thread?.pinned ? 'club-board-post--pinned' : '',
        expanded ? 'club-board-post--expanded' : '',
      ].filter(Boolean).join(' ')}
      data-testid={`club-board-post-${thread.id}`}
    >
      <div className="club-board-post__main">
        <ClubBoardThreadHeader thread={thread} />

        {expanded && (
          <>
            {loadingDetail && !bodyText && (
              <div className="club-board-post__loading">
                <Spinner animation="border" size="sm" className="me-2" />
                <span className="text-muted small">Loading post…</span>
              </div>
            )}
            {detailError && !loadingDetail && (
              <p className="club-board-post__error small text-danger mb-0">{detailError}</p>
            )}
            {bodyText && <p className="club-board-post__body">{bodyText}</p>}
          </>
        )}

        <div className="club-board-post__actions">
          <button
            type="button"
            className="club-board-post__action"
            onClick={(e) => {
              e.preventDefault();
              onToggleExpand?.();
            }}
            aria-expanded={expanded}
          >
            <FaComment aria-hidden="true" />
            <span>
              {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
            </span>
            {expanded ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}
          </button>
          <div className="club-board-post__actions-end">
            <Link
              to={`/clubs/${clubId}/board/${thread.id}`}
              className="club-board-post__open-thread"
            >
              Open thread
            </Link>
            <button type="button" className="club-board-post__reply-btn" onClick={openReply}>
              Reply
            </button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="club-board-post__replies">
          {loadingDetail && detail && (
            <div className="club-board-post__loading py-2">
              <Spinner animation="border" size="sm" />
            </div>
          )}
          {!loadingDetail && !detailError && (
            <ClubBoardReplyList replies={replies} />
          )}
          {!loadingDetail && detailError && (
            <p className="club-board-post__error small text-danger mb-0">{detailError}</p>
          )}

          {replyOpen && (
            <ClubBoardReplyComposer
              currentUser={currentUser}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={handleReplyKeyDown}
              onSubmit={handleReply}
              saving={replySaving}
            />
          )}
        </div>
      )}
    </article>
  );
}

ClubBoardPostCard.propTypes = {
  thread: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    title: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    pinned: PropTypes.bool,
    reply_count: PropTypes.number,
    created_by: PropTypes.object,
    last_posted_at: PropTypes.string,
    updated_at: PropTypes.string,
    created_at: PropTypes.string,
  }).isRequired,
  clubId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  currentUser: PropTypes.object,
  expanded: PropTypes.bool,
  onToggleExpand: PropTypes.func,
  onReplyPosted: PropTypes.func,
};
